// src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  onAuthStateChanged,
} from "firebase/auth";
import { auth, db } from "../services/firebase";
import { doc, getDoc, onSnapshot } from "firebase/firestore";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

/* ---------------- helpers ---------------- */
const normalizeRole = (raw) => {
  const r = String(raw || "").trim().toLowerCase();
  if (!r) return "";
  if (r === "admin") return "superadmin";
  if (r === "school_admin" || r === "school") return "schooladmin";
  if (["superadmin", "schooladmin", "teacher", "student"].includes(r)) return r;
  return r;
};

const withDerived = (user, docData) => {
  const role = normalizeRole(docData?.role);

  return {
    id: user?.uid || docData?.id || null,
    uid: user?.uid || docData?.id || null,
    email: docData?.email ?? user?.email ?? "",
    displayName: docData?.displayName ?? user?.displayName ?? "",
    photoURL: docData?.photoURL ?? user?.photoURL ?? "",
    role,
    schoolId: docData?.schoolId ?? "",
    active: docData?.active !== false,
    ...docData, // keep any extra fields you store
  };
};

// Fees local unlock keys (so we can clear them on logout)
const feeKey = (schoolId, uid) => `feeAuth:${schoolId}:${uid}`;
const clearAllFeeAuthFor = (uid, schoolId) => {
  try {
    if (!uid || !schoolId) return;
    const k = feeKey(schoolId, uid);
    sessionStorage.removeItem(k);
    localStorage.removeItem(k);
  } catch {}
};

/**
 * AuthProvider
 * - user: Firebase Auth user (or null)
 * - profile: merged {users/{uid}} + auth fields; undefined while loading subscription
 * - authReady: true after initial onAuthStateChanged resolves
 * - profileReady: true once profile is loaded (profile !== undefined)
 * - role helpers: isSuperAdmin, isSchoolAdmin, isTeacher, isStudent
 * - login(email, pw): returns { user, profile }
 * - logout(): signs out and clears fee unlock cache for this user
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(undefined); // undefined => profile subscription not resolved yet
  const [authReady, setAuthReady] = useState(false);  // initial auth handshake
  const profileUnsubRef = useRef(null);

  // Subscribe to Firebase Auth; then subscribe to users/{uid}
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      setUser(u || null);

      // Clean any previous profile listener
      if (profileUnsubRef.current) {
        profileUnsubRef.current();
        profileUnsubRef.current = null;
      }

      if (u) {
        setProfile(undefined); // signal that profile is loading

        profileUnsubRef.current = onSnapshot(
          doc(db, "users", u.uid),
          (snap) => {
            const docData = snap.exists() ? { id: snap.id, ...snap.data() } : null;
            const merged = docData ? withDerived(u, docData) : null; // null if no profile doc
            setProfile(merged);
          },
          (err) => {
    console.error("Profile snapshot failed:", err);
    setProfile(null);
  }
        );
      } else {
        // Signed out
        setProfile(null);
      }

      setAuthReady(true);
    });

    return () => {
      unsubAuth();
      if (profileUnsubRef.current) profileUnsubRef.current();
    };
  }, []);

  // Sign in and return both user + profile immediately
  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);

    // Prime profile early so routes can redirect right away
    try {
      const snap = await getDoc(doc(db, "users", cred.user.uid));
      const docData = snap.exists() ? { id: snap.id, ...snap.data() } : null;
      const merged = docData ? withDerived(cred.user, docData) : null;
      setProfile(merged);
    } catch {
      // Profile listener will fill it in shortly
    }

    setUser(cred.user);
    return { user: cred.user, profile: profile ?? null };
  };

  const logout = async () => {
    // Clear the fee unlock cache for this signed-in user/school (if any)
    const uid = user?.uid || profile?.uid || null;
    const schoolId = profile?.schoolId || null;
    clearAllFeeAuthFor(uid, schoolId);

    await fbSignOut(auth);
    setProfile(null);
    setUser(null);
  };

  // Derived flags
  const role = normalizeRole(profile?.role);
  const isSuperAdmin = role === "superadmin";
  const isSchoolAdmin = role === "schooladmin";
  const isTeacher = role === "teacher";
  const isStudent = role === "student";

  // Readiness booleans for components/guards
  const profileReady = profile !== undefined;

  const value = {
    user,
    profile,       // undefined while loading; null or object afterwards
    authReady,     // initial auth ready
    profileReady,  // user profile subscription resolved
    role,
    isSuperAdmin,
    isSchoolAdmin,
    isTeacher,
    isStudent,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
